import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Menu, X } from "lucide-react";
import type { Href, SiteNode } from "@zirro/schema";
import type { RenderContext } from "./types";
import { resolveNodeProps, fontSizeValue, resolveFontFamily } from "./theme";
import { getRegistryEntry } from "./registry";

export function linkToHref(href: Href, ctx: RenderContext): string {
  if (href.type === "url") return href.url;
  const page = ctx.document?.pages?.find((p) => p.id === href.pageId);
  return page ? page.slug : "#";
}

export function SmartLink({
  href,
  ctx,
  className,
  style,
  children,
}: {
  href: Href;
  ctx: RenderContext;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const to = linkToHref(href, ctx);
  const isInternal =
    href.type === "page" ||
    (href.type === "url" &&
      (href.url === "/" ||
        href.url.startsWith("/") ||
        href.url.startsWith("#") ||
        Boolean(ctx.document?.pages?.some((p) => p.slug === href.url))));

  return (
    <a
      href={to}
      className={className}
      style={style}
      onClick={(event) => {
        if (ctx.onNavigate) {
          if (isInternal) {
            event.preventDefault();
            ctx.onNavigate(href);
          }
        } else if (ctx.mode !== "public") {
          if (isInternal) {
            event.preventDefault();
          }
        }
      }}
      rel={isInternal ? undefined : "noopener noreferrer"}
    >
      {children}
    </a>
  );
}

interface Common {
  node: SiteNode;
  ctx: RenderContext;
}

function useProps(node: SiteNode, ctx: RenderContext) {
  const { props, forced } = resolveNodeProps(node, ctx.breakpoint);
  const edit = ctx.mode === "edit";
  const isSelected = edit && ctx.selectedNodeId === node.id;
  const isHovered = edit && ctx.hoveredNodeId === node.id;
  const dataProps = edit
    ? {
        "data-node-id": node.id,
        "data-node-type": node.type,
        "data-selected": isSelected ? "true" : undefined,
        "data-hovered": isHovered ? "true" : undefined,
      }
    : {};

  const outlineStyle: CSSProperties =
    edit && (isSelected || isHovered)
      ? {
          position: "relative",
          outline: isSelected ? "2px solid #6366f1" : "1px dashed #818cf8",
          outlineOffset: isSelected ? "2px" : "1px",
          transition: "outline 150ms ease, outline-offset 150ms ease",
        }
      : {};

  const style: CSSProperties = { ...(forced ?? {}), ...outlineStyle };
  return { props, dataProps, style, isSelected, isHovered };
}

const COLOR_VAR: Record<string, string> = {
  primary: "var(--zirro-color-primary)",
  secondary: "var(--zirro-color-secondary)",
  accent: "var(--zirro-color-accent)",
  background: "var(--zirro-color-background)",
  surface: "var(--zirro-color-surface)",
  text: "var(--zirro-color-text)",
  muted: "var(--zirro-color-muted)",
  border: "var(--zirro-color-border)",
};

const SPACING: Record<string, string> = {
  none: "0px",
  XS: "4px",
  SM: "8px",
  MD: "16px",
  LG: "32px",
  XL: "64px",
};

const RADII_VAL: Record<string, string> = {
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "16px",
  xl: "24px",
  full: "9999px",
  theme: "var(--zirro-radius, 8px)",
};

/** Shadow presets for cards; other strings are used as CSS box-shadow. */
const SHADOWS: Record<string, string> = {
  none: "none",
  sm: "0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.08)",
  md: "0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)",
  lg: "0 18px 40px -12px rgba(15, 23, 42, 0.22)",
};

function resolveSpacingValue(val: unknown, fallback: string = "0px"): string {
  if (typeof val === "string") {
    if (val in SPACING) return SPACING[val];
    if (val.trim()) return val;
  }
  if (typeof val === "number") return `${val}px`;
  return fallback;
}

function resolveColorValue(color: unknown): string | undefined {
  if (
    typeof color !== "string" ||
    !color.trim() ||
    color === "none" ||
    color === "transparent"
  ) {
    return color === "transparent" ? "transparent" : undefined;
  }
  if (COLOR_VAR[color]) return COLOR_VAR[color];
  return color;
}

function resolveSectionBackground(props: Record<string, unknown>): {
  background?: string;
  backgroundColor?: string;
  backgroundImage?: string;
} {
  // Direct gradient override
  if (
    typeof props.backgroundGradient === "string" &&
    props.backgroundGradient.trim()
  ) {
    return { background: props.backgroundGradient };
  }
  if (props.gradientType === "linear") {
    const angle = Number(props.gradientAngle ?? 135);
    const from = resolveColorValue(props.gradientFrom) ?? "#4f46e5";
    const to = resolveColorValue(props.gradientTo) ?? "#ec4899";
    return { background: `linear-gradient(${angle}deg, ${from}, ${to})` };
  }
  if (props.gradientType === "radial") {
    const from = resolveColorValue(props.gradientFrom) ?? "#4f46e5";
    const to = resolveColorValue(props.gradientTo) ?? "#ec4899";
    return { background: `radial-gradient(circle, ${from}, ${to})` };
  }

  const bgProp = props.backgroundColor ?? props.background;
  const resolvedBg = resolveColorValue(bgProp);

  const imgUrl =
    typeof props.backgroundImage === "string" && props.backgroundImage.trim()
      ? props.backgroundImage
      : null;

  if (imgUrl) {
    return {
      backgroundImage: `url("${imgUrl}")`,
      backgroundColor: resolvedBg ?? "transparent",
    };
  }

  if (resolvedBg) {
    return { backgroundColor: resolvedBg };
  }

  return {};
}

function resolveBorderStyle(props: Record<string, unknown>): CSSProperties {
  const borderPos = String(props.borderPosition ?? "none");
  if (borderPos === "none" && !props.borderWidth) return {};

  const width = props.borderWidth
    ? `${String(props.borderWidth).replace("px", "")}px`
    : "1px";
  const color =
    resolveColorValue(props.borderColor) ??
    "var(--zirro-color-border, #e2e8f0)";
  const borderVal = `${width} solid ${color}`;

  if (borderPos === "top") return { borderTop: borderVal };
  if (borderPos === "bottom") return { borderBottom: borderVal };
  if (borderPos === "y")
    return { borderTop: borderVal, borderBottom: borderVal };
  if (borderPos === "all") return { border: borderVal };
  return {};
}

function resolveContentMaxWidth(props: Record<string, unknown>): string {
  if (props.contentWidth === "narrow") return "640px";
  if (props.contentWidth === "normal") return "768px";
  if (props.contentWidth === "wide") return "1140px";
  if (props.contentWidth === "full") return "100%";
  if (typeof props.customMaxWidth === "string" && props.customMaxWidth.trim()) {
    const v = props.customMaxWidth.trim();
    return v.includes("px") || v.includes("%") || v.includes("rem")
      ? v
      : `${v}px`;
  }
  return props.width === "full" ? "1024px" : "100%";
}

function resolveMinHeight(val: unknown): string | undefined {
  if (typeof val !== "string" || !val.trim() || val === "auto")
    return undefined;
  if (val === "screen") return "100vh";
  if (val === "lg") return "600px";
  if (val === "md") return "400px";
  if (val === "sm") return "240px";
  return val.includes("px") || val.includes("vh") ? val : `${val}px`;
}

function containerStyle(props: Record<string, unknown>): CSSProperties {
  const style: CSSProperties = {
    display: "flex",
    flexDirection: props.direction === "horizontal" ? "row" : "column",
    gap: SPACING[String(props.gap)] ?? SPACING.MD,
    // Phones cap card padding so text keeps a readable width.
    padding: `min(${SPACING[String(props.padding)] ?? "0px"}, var(--zirro-card-pad-max, 100vh))`,
    alignItems:
      props.align === "center"
        ? "center"
        : props.align === "end"
          ? "flex-end"
          : props.align === "start"
            ? "flex-start"
            : "stretch",
    justifyContent:
      props.justify === "center"
        ? "center"
        : props.justify === "end"
          ? "flex-end"
          : props.justify === "between"
            ? "space-between"
            : "flex-start",
    maxWidth: props.width === "contained" ? "720px" : "100%",
    margin: props.width === "contained" ? "0 auto" : undefined,
    textAlign:
      typeof props.align === "string" && props.align === "center"
        ? "center"
        : undefined,
    boxSizing: "border-box",
  };
  if (props.direction === "horizontal") style.flexWrap = "wrap";

  // Card styling: background, text colour, border, corners and shadow.
  const background = resolveColorValue(props.background);
  if (background) style.background = background;
  const textColor = resolveColorValue(props.textColor);
  if (textColor) {
    style.color = textColor;
    Object.assign(style, {
      "--zirro-section-heading-color": textColor,
      "--zirro-section-body-color": textColor,
      "--zirro-section-text-color": textColor,
    });
  }
  if (props.borderWidth && props.borderWidth !== "0px") {
    style.border = `${String(props.borderWidth)} solid ${resolveColorValue(props.borderColor) ?? "var(--zirro-color-border)"}`;
  }
  if (typeof props.borderRadius === "string" && props.borderRadius) {
    style.borderRadius = RADII_VAL[props.borderRadius] ?? props.borderRadius;
    // Rounded cards clip photos to their corners.
    style.overflow = "hidden";
  }
  if (typeof props.boxShadow === "string" && props.boxShadow) {
    style.boxShadow = SHADOWS[props.boxShadow] ?? props.boxShadow;
  }
  if (typeof props.minHeight === "string" && props.minHeight)
    style.minHeight = props.minHeight;
  return style;
}

export function Section({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);

  // Spacing calculation
  const basePadding = SPACING[String(props.padding)] ?? SPACING.LG;
  const pTop =
    props.paddingTop !== undefined
      ? resolveSpacingValue(props.paddingTop)
      : basePadding;
  const pBottom =
    props.paddingBottom !== undefined
      ? resolveSpacingValue(props.paddingBottom)
      : basePadding;
  const pLeft =
    props.paddingLeft !== undefined
      ? resolveSpacingValue(props.paddingLeft)
      : basePadding === "0px"
        ? undefined
        : "24px";
  const pRight =
    props.paddingRight !== undefined
      ? resolveSpacingValue(props.paddingRight)
      : basePadding === "0px"
        ? undefined
        : "24px";

  const mTop = resolveSpacingValue(props.marginTop, "0px");
  const mBottom = resolveSpacingValue(props.marginBottom, "0px");
  const mLeft = resolveSpacingValue(
    props.marginLeft,
    props.width === "contained" ? "auto" : "0px",
  );
  const mRight = resolveSpacingValue(
    props.marginRight,
    props.width === "contained" ? "auto" : "0px",
  );

  const bgStyles = resolveSectionBackground(props);
  const borderStyles = resolveBorderStyle(props);
  const minHeightVal = resolveMinHeight(props.minHeight);
  const contentMaxWidth = resolveContentMaxWidth(props);
  const cols = Math.min(Math.max(Number(props.columns ?? 1), 1), 4);

  // Overlay calculation
  const hasOverlay =
    Boolean(props.overlayEnabled) ||
    (props.overlayOpacity !== undefined && Number(props.overlayOpacity) > 0);
  const overlayOpacity =
    props.overlayOpacity !== undefined
      ? Math.min(Math.max(Number(props.overlayOpacity), 0), 100) / 100
      : 0.5;
  const overlayColor = resolveColorValue(props.overlayColor) ?? "#000000";

  let overlayBackground: string | undefined = undefined;
  if (
    typeof props.overlayGradient === "string" &&
    props.overlayGradient.trim()
  ) {
    overlayBackground = props.overlayGradient;
  } else if (props.overlayDirection && props.overlayDirection !== "none") {
    const dir = String(props.overlayDirection);
    if (dir === "to-right") {
      overlayBackground = `linear-gradient(to right, ${overlayColor} 0%, transparent 100%)`;
    } else if (dir === "to-left") {
      overlayBackground = `linear-gradient(to left, ${overlayColor} 0%, transparent 100%)`;
    } else if (dir === "to-bottom") {
      overlayBackground = `linear-gradient(to bottom, ${overlayColor} 0%, transparent 100%)`;
    } else if (dir === "to-top") {
      overlayBackground = `linear-gradient(to top, ${overlayColor} 0%, transparent 100%)`;
    } else if (dir === "to-bottom-right") {
      overlayBackground = `linear-gradient(135deg, ${overlayColor} 0%, transparent 100%)`;
    } else if (dir === "radial") {
      overlayBackground = `radial-gradient(circle, transparent 20%, ${overlayColor} 100%)`;
    }
  }

  const textColor = resolveColorValue(props.textColor);
  const sectionHeadingColor =
    resolveColorValue(props.headingColor) ?? textColor;
  const sectionBodyColor = resolveColorValue(props.bodyColor) ?? textColor;
  const sectionLinkColor = resolveColorValue(props.linkColor);
  const sectionBtnColor = resolveColorValue(props.buttonColor);
  const sectionFontFamily = resolveFontFamily(props.fontFamily);

  const isMobile = ctx.breakpoint === "mobile";
  const isTablet = ctx.breakpoint === "tablet";
  const isProductSlot =
    node.id.includes("__products") ||
    node.id.includes("products") ||
    node.id.includes("best") ||
    node.id.includes("new");
  const effectiveCols = isMobile
    ? isProductSlot
      ? Math.min(cols, 2)
      : 1
    : isTablet
      ? cols === 4
        ? 2
        : cols
      : cols;

  const finalPLeft =
    isMobile && props.paddingLeft === undefined && pLeft !== undefined
      ? "16px"
      : pLeft;
  const finalPRight =
    isMobile && props.paddingRight === undefined && pRight !== undefined
      ? "16px"
      : pRight;

  const wrapperStyle: CSSProperties = {
    position: "relative",
    width: props.width === "contained" ? "calc(100% - 32px)" : "100%",
    maxWidth:
      props.width === "contained"
        ? props.customMaxWidth
          ? String(props.customMaxWidth)
          : "1200px"
        : "100%",
    marginTop: mTop !== "0px" ? mTop : undefined,
    marginBottom: mBottom !== "0px" ? mBottom : undefined,
    marginLeft: mLeft !== "0px" ? mLeft : undefined,
    marginRight: mRight !== "0px" ? mRight : undefined,
    // Phones cap generous desktop spacing (see --zirro-section-pad-max).
    paddingTop: `min(${pTop}, var(--zirro-section-pad-max, 100vh))`,
    paddingBottom: `min(${pBottom}, var(--zirro-section-pad-max, 100vh))`,
    paddingLeft: finalPLeft,
    paddingRight: finalPRight,
    minHeight: minHeightVal,
    color: textColor,
    ...(sectionHeadingColor
      ? ({ "--zirro-section-heading-color": sectionHeadingColor } as Record<
          string,
          string
        >)
      : {}),
    ...(sectionBodyColor
      ? ({ "--zirro-section-body-color": sectionBodyColor } as Record<
          string,
          string
        >)
      : {}),
    ...(sectionLinkColor
      ? ({ "--zirro-section-link-color": sectionLinkColor } as Record<
          string,
          string
        >)
      : {}),
    ...(sectionBtnColor
      ? ({ "--zirro-section-btn-color": sectionBtnColor } as Record<
          string,
          string
        >)
      : {}),
    ...(sectionFontFamily
      ? ({ "--zirro-section-font-family": sectionFontFamily } as Record<
          string,
          string
        >)
      : {}),
    ...bgStyles,
    backgroundSize:
      (props.backgroundSize as CSSProperties["backgroundSize"]) ?? "cover",
    backgroundPosition:
      (props.backgroundPosition as CSSProperties["backgroundPosition"]) ??
      "center",
    backgroundRepeat:
      (props.backgroundRepeat as CSSProperties["backgroundRepeat"]) ??
      "no-repeat",
    backgroundAttachment:
      (props.backgroundAttachment as CSSProperties["backgroundAttachment"]) ??
      "scroll",
    borderRadius: props.borderRadius
      ? (RADII_VAL[String(props.borderRadius)] ?? String(props.borderRadius))
      : undefined,
    boxShadow:
      typeof props.boxShadow === "string" ? props.boxShadow : undefined,
    ...borderStyles,
    overflow: "hidden",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent:
      props.justify === "center"
        ? "center"
        : props.justify === "end"
          ? "flex-end"
          : props.justify === "between"
            ? "space-between"
            : "flex-start",
    alignItems:
      props.align === "center"
        ? "center"
        : props.align === "end"
          ? "flex-end"
          : props.align === "start"
            ? "flex-start"
            : "stretch",
    textAlign:
      props.align === "center"
        ? "center"
        : props.align === "end" || props.align === "right"
          ? "right"
          : undefined,
    ...style,
  };

  const innerStyle: CSSProperties = {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: contentMaxWidth,
    margin: "0 auto",
    display: cols > 1 ? "grid" : "flex",
    gridTemplateColumns:
      effectiveCols > 1
        ? `repeat(${effectiveCols}, minmax(0, 1fr))`
        : undefined,
    flexDirection: props.direction === "horizontal" ? "row" : "column",
    flexWrap: props.direction === "horizontal" ? "wrap" : undefined,
    gap:
      SPACING[String(props.gap)] ??
      (typeof props.gap === "string" ? props.gap : SPACING.MD),
    alignItems:
      props.align === "center"
        ? "center"
        : props.align === "end"
          ? "flex-end"
          : props.align === "start"
            ? "flex-start"
            : "stretch",
    justifyContent:
      props.justify === "center"
        ? "center"
        : props.justify === "end"
          ? "flex-end"
          : props.justify === "between"
            ? "space-between"
            : "flex-start",
    boxSizing: "border-box",
  };

  return (
    <section
      {...dataProps}
      data-section-id={node.id}
      data-cols={cols}
      style={wrapperStyle}
    >
      {hasOverlay ? (
        <div
          data-section-overlay
          style={{
            position: "absolute",
            inset: 0,
            background: overlayBackground,
            backgroundColor: overlayBackground ? undefined : overlayColor,
            opacity: overlayOpacity,
            pointerEvents: "none",
            zIndex: 0,
            transition: "opacity 150ms ease, background-color 150ms ease",
          }}
        />
      ) : null}
      <div data-section-inner="true" style={innerStyle}>
        {node.children && node.children.length > 0 ? (
          node.children.map((child) => (
            <RenderChild key={child.id} node={child} ctx={ctx} />
          ))
        ) : ctx.mode === "edit" ? (
          <div
            data-edit-only
            style={{
              padding: "32px 16px",
              width: "100%",
              textAlign: "center",
              border: "1px dashed var(--zirro-color-border, #94a3b8)",
              borderRadius: "8px",
              color: "var(--zirro-color-muted, #64748b)",
              fontSize: "13px",
              background: "rgba(99, 102, 241, 0.04)",
            }}
          >
            Empty section — Add components from the left panel
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function Container({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const element = (
    <div {...dataProps} style={{ ...containerStyle(props), ...style }}>
      {node.children?.map((child) => (
        <RenderChild key={child.id} node={child} ctx={ctx} />
      ))}
    </div>
  );

  if (props.href && typeof props.href === "object") {
    return (
      <SmartLink
        href={props.href as Href}
        ctx={ctx}
        style={{
          textDecoration: "none",
          color: "inherit",
          display: "block",
          width: "100%",
        }}
      >
        {element}
      </SmartLink>
    );
  }

  // Automatic click-through for template product containers in preview or public mode
  if (ctx.mode !== "edit" && !props.href) {
    const isProductContainer =
      node.id.includes("_p1") ||
      node.id.includes("_p2") ||
      node.id.includes("_p3") ||
      node.id.includes("_p4") ||
      node.id.includes("_p5") ||
      node.id.includes("_p6") ||
      node.id.includes("_best_p") ||
      node.id.includes("_new_p") ||
      node.id.includes("prod_") ||
      node.id.includes("product_");

    if (
      isProductContainer &&
      ctx.document.pages.some((p) => p.slug === "/shop")
    ) {
      return (
        <SmartLink
          href={{ type: "url", url: "/shop" }}
          ctx={ctx}
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "block",
            width: "100%",
            cursor: "pointer",
          }}
        >
          {element}
        </SmartLink>
      );
    }
  }

  return element;
}

export function Heading({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const level = Number(props.level ?? 2);
  const Tag = `h${Math.min(Math.max(level, 1), 6)}` as "h1";
  const isInlineEditing =
    ctx.mode === "edit" && ctx.inlineEditingNodeId === node.id;

  const resolvedColor =
    resolveColorValue(props.textColor || props.color) ??
    "var(--zirro-section-heading-color, var(--zirro-section-text-color, var(--zirro-color-text)))";
  const resolvedFont =
    resolveFontFamily(props.fontFamily) ??
    "var(--zirro-section-font-family, var(--zirro-font-heading))";
  const mTop = props.marginTop ? resolveSpacingValue(props.marginTop) : "0px";
  const mBottom = props.marginBottom
    ? resolveSpacingValue(props.marginBottom)
    : "12px";
  const bg = resolveColorValue(props.backgroundColor);
  const pad = props.padding ? resolveSpacingValue(props.padding) : undefined;
  const radius = props.borderRadius
    ? (RADII_VAL[String(props.borderRadius)] ?? String(props.borderRadius))
    : undefined;

  if (isInlineEditing && ctx.onUpdateNodeProps) {
    return (
      <input
        type="text"
        {...dataProps}
        autoFocus
        value={String(props.text ?? "")}
        onChange={(e) =>
          ctx.onUpdateNodeProps?.(node.id, { text: e.target.value })
        }
        onBlur={() => ctx.onInlineEditChange?.(null)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") {
            ctx.onInlineEditChange?.(null);
          }
        }}
        style={{
          font: "inherit",
          fontFamily: resolvedFont,
          fontSize: fontSizeValue(props.fontSize ?? "3xl"),
          fontWeight: Number(props.weight ?? 700),
          color: resolvedColor,
          width: "100%",
          background: "rgba(99, 102, 241, 0.12)",
          border: "2px solid #6366f1",
          borderRadius: "4px",
          padding: "4px 8px",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    );
  }

  return (
    <Tag
      {...dataProps}
      onDoubleClick={(e) => {
        if (ctx.mode === "edit") {
          e.stopPropagation();
          ctx.onInlineEditChange?.(node.id);
        }
      }}
      style={{
        color: resolvedColor,
        textAlign: (props.align as CSSProperties["textAlign"]) ?? "left",
        fontSize: fontSizeValue(props.fontSize ?? "3xl"),
        fontWeight: Number(props.weight ?? 700),
        marginTop: mTop,
        marginBottom: mBottom,
        fontFamily: resolvedFont,
        lineHeight: props.lineHeight ? Number(props.lineHeight) : 1.25,
        letterSpacing: props.letterSpacing
          ? String(props.letterSpacing)
          : undefined,
        textTransform:
          (props.textTransform as CSSProperties["textTransform"]) ?? undefined,
        textDecoration:
          (props.textDecoration as CSSProperties["textDecoration"]) ??
          undefined,
        maxWidth: props.maxWidth ? String(props.maxWidth) : undefined,
        backgroundColor: bg,
        padding: pad,
        borderRadius: radius,
        display: bg ? "inline-block" : undefined,
        ...style,
      }}
    >
      {String(props.text ?? "")}
    </Tag>
  );
}

export interface TextRun {
  text: string;
  marks?: ("bold" | "italic" | "underline" | "code")[];
  link?: Href;
}

function renderRun(run: TextRun, key: number, ctx: RenderContext): ReactNode {
  let node: ReactNode = run.text;
  if (run.marks?.includes("bold")) node = <strong>{node}</strong>;
  if (run.marks?.includes("italic")) node = <em>{node}</em>;
  if (run.marks?.includes("underline")) node = <u>{node}</u>;
  if (run.marks?.includes("code"))
    node = (
      <code style={{ fontFamily: "monospace", fontSize: "0.9em" }}>{node}</code>
    );
  if (run.link) {
    node = (
      <SmartLink
        href={run.link}
        ctx={ctx}
        style={{ color: "var(--zirro-color-primary)" }}
      >
        {node}
      </SmartLink>
    );
  }
  return <span key={key}>{node}</span>;
}

export function Text({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const content = Array.isArray(props.content)
    ? (props.content as TextRun[])
    : [];
  const textValue = content[0]?.text ?? "";
  const isInlineEditing =
    ctx.mode === "edit" && ctx.inlineEditingNodeId === node.id;

  const resolvedColor =
    resolveColorValue(props.textColor || props.color) ??
    "var(--zirro-section-body-color, var(--zirro-section-text-color, var(--zirro-color-text)))";
  const resolvedFont =
    resolveFontFamily(props.fontFamily) ??
    "var(--zirro-section-font-family, var(--zirro-font-body))";
  const mTop = props.marginTop ? resolveSpacingValue(props.marginTop) : "0px";
  const mBottom = props.marginBottom
    ? resolveSpacingValue(props.marginBottom)
    : "16px";
  const bg = resolveColorValue(props.backgroundColor);
  const pad = props.padding ? resolveSpacingValue(props.padding) : undefined;
  const radius = props.borderRadius
    ? (RADII_VAL[String(props.borderRadius)] ?? String(props.borderRadius))
    : undefined;

  if (isInlineEditing && ctx.onUpdateNodeProps) {
    return (
      <textarea
        {...dataProps}
        autoFocus
        value={textValue}
        onChange={(e) =>
          ctx.onUpdateNodeProps?.(node.id, {
            content: [{ text: e.target.value }],
          })
        }
        onBlur={() => ctx.onInlineEditChange?.(null)}
        rows={3}
        style={{
          font: "inherit",
          fontFamily: resolvedFont,
          fontSize: fontSizeValue(props.size ?? "base"),
          fontWeight: Number(props.weight ?? 400),
          color: resolvedColor,
          width: "100%",
          background: "rgba(99, 102, 241, 0.12)",
          border: "2px solid #6366f1",
          borderRadius: "4px",
          padding: "6px 8px",
          outline: "none",
          boxSizing: "border-box",
          resize: "vertical",
        }}
      />
    );
  }

  return (
    <p
      {...dataProps}
      onDoubleClick={(e) => {
        if (ctx.mode === "edit") {
          e.stopPropagation();
          ctx.onInlineEditChange?.(node.id);
        }
      }}
      style={{
        color: resolvedColor,
        textAlign: (props.align as CSSProperties["textAlign"]) ?? "left",
        fontSize: fontSizeValue(props.size ?? "base"),
        fontWeight: Number(props.weight ?? 400),
        marginTop: mTop,
        marginBottom: mBottom,
        lineHeight: props.lineHeight ? Number(props.lineHeight) : 1.6,
        letterSpacing: props.letterSpacing
          ? String(props.letterSpacing)
          : undefined,
        textTransform:
          (props.textTransform as CSSProperties["textTransform"]) ?? undefined,
        textDecoration:
          (props.textDecoration as CSSProperties["textDecoration"]) ??
          undefined,
        maxWidth: props.maxWidth ? String(props.maxWidth) : undefined,
        backgroundColor: bg,
        padding: pad,
        borderRadius: radius,
        display: bg ? "inline-block" : undefined,
        fontFamily: resolvedFont,
        ...style,
      }}
    >
      {content.length === 1 && !content[0].marks?.length && !content[0].link
        ? content[0].text
        : content.map((run, i) => renderRun(run, i, ctx))}
    </p>
  );
}

export function Card({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  return (
    <div
      {...dataProps}
      style={{
        background:
          props.variant === "primary"
            ? "var(--zirro-color-primary)"
            : "var(--zirro-color-surface)",
        color:
          props.variant === "primary" ? "#ffffff" : "var(--zirro-color-text)",
        border: "1px solid var(--zirro-color-border)",
        borderRadius: "var(--zirro-radius, 12px)",
        padding: SPACING[String(props.padding)] ?? SPACING.MD,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        boxSizing: "border-box",
        textAlign: (props.align as CSSProperties["textAlign"]) ?? "left",
        ...style,
      }}
    >
      {props.title ? (
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "20px",
            fontWeight: 700,
            fontFamily: "var(--zirro-font-heading)",
          }}
        >
          {String(props.title)}
        </h3>
      ) : null}
      {props.description ? (
        <p
          style={{
            margin: "0 0 12px 0",
            color: "var(--zirro-color-muted)",
            fontSize: "14px",
            lineHeight: 1.5,
          }}
        >
          {String(props.description)}
        </p>
      ) : null}
      {node.children?.map((child) => (
        <RenderChild key={child.id} node={child} ctx={ctx} />
      ))}
    </div>
  );
}

export function FeatureGrid({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const cols = Number(props.columns ?? 3);
  const isMobile = ctx.breakpoint === "mobile";
  const isTablet = ctx.breakpoint === "tablet";
  const effectiveCols = isMobile ? 1 : isTablet ? Math.min(cols, 2) : cols;
  return (
    <div
      {...dataProps}
      data-feature-grid="true"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${effectiveCols}, minmax(0, 1fr))`,
        gap: SPACING[String(props.gap)] ?? SPACING.MD,
        width: "100%",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {node.children?.map((child) => (
        <RenderChild key={child.id} node={child} ctx={ctx} />
      ))}
    </div>
  );
}

export function Badge({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const isInlineEditing =
    ctx.mode === "edit" && ctx.inlineEditingNodeId === node.id;

  if (isInlineEditing && ctx.onUpdateNodeProps) {
    return (
      <input
        type="text"
        {...dataProps}
        autoFocus
        value={String(props.text ?? "")}
        onChange={(e) =>
          ctx.onUpdateNodeProps?.(node.id, { text: e.target.value })
        }
        onBlur={() => ctx.onInlineEditChange?.(null)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape")
            ctx.onInlineEditChange?.(null);
        }}
        style={{
          padding: "4px 8px",
          fontSize: "12px",
          borderRadius: "9999px",
          border: "2px solid #6366f1",
          background: "rgba(99, 102, 241, 0.12)",
          color: "#ffffff",
          outline: "none",
        }}
      />
    );
  }

  return (
    <span
      {...dataProps}
      onDoubleClick={(e) => {
        if (ctx.mode === "edit") {
          e.stopPropagation();
          ctx.onInlineEditChange?.(node.id);
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: props.size === "lg" ? "6px 14px" : "4px 10px",
        fontSize: props.size === "lg" ? "14px" : "12px",
        fontWeight: 600,
        borderRadius: "9999px",
        background:
          props.variant === "accent"
            ? "var(--zirro-color-accent)"
            : "var(--zirro-color-primary)",
        color: "#ffffff",
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {String(props.text ?? "Badge")}
    </span>
  );
}

export function Divider({ node, ctx }: Common) {
  const { dataProps, style } = useProps(node, ctx);
  return (
    <hr
      {...dataProps}
      style={{
        border: "none",
        borderTop: "1px solid var(--zirro-color-border)",
        margin: "16px 0",
        width: "100%",
        ...style,
      }}
    />
  );
}

export function Spacer({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const height = SPACING[String(props.height)] ?? "32px";
  return (
    <div
      {...dataProps}
      style={{
        height,
        width: "100%",
        ...style,
      }}
    />
  );
}

/** Frame shapes for images (width / height); "original" keeps the photo's own shape. */
const FRAME_RATIOS: Record<string, number> = {
  "1:1": 1,
  "4:5": 4 / 5,
  "3:4": 3 / 4,
  "4:3": 4 / 3,
  "3:2": 3 / 2,
  "16:9": 16 / 9,
  "21:9": 21 / 9,
};

function percent(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(100, Math.max(0, value))
    : 50;
}

export function Image({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const src = typeof props.src === "string" && props.src ? props.src : null;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = src !== null && failedSrc === src;
  const alt = String(props.alt ?? "");
  // The frame: a fixed shape the photo fills (cover) or sits inside (contain),
  // cropped around the focal point and optionally zoomed in.
  const ratio = FRAME_RATIOS[String(props.aspectRatio)] ?? null;
  const fit = props.fit === "contain" ? "contain" : "cover";
  const focus = `${percent(props.focalX)}% ${percent(props.focalY)}%`;
  const zoom =
    typeof props.zoom === "number" ? Math.min(3, Math.max(1, props.zoom)) : 1;
  const radius = props.rounded ? "var(--zirro-radius)" : undefined;
  const wrapper: CSSProperties = {
    display: "flex",
    justifyContent:
      props.align === "left"
        ? "flex-start"
        : props.align === "right"
          ? "flex-end"
          : "center",
    maxWidth:
      props.width === "full"
        ? "100%"
        : props.width === "half"
          ? "50%"
          : "480px",
    margin: props.align === "center" ? "0 auto" : undefined,
    ...style,
  };

  if (!src || failed) {
    // Missing/broken media degrades gracefully in every mode (PRD §160).
    return (
      <figure {...dataProps} style={{ ...wrapper, margin: wrapper.margin }}>
        <div
          style={{
            background: "var(--zirro-color-surface, #f1f5f9)",
            border: "1px dashed var(--zirro-color-border, #cbd5e1)",
            borderRadius: "var(--zirro-radius, 8px)",
            width: "100%",
            minHeight: ratio ? undefined : "120px",
            aspectRatio: ratio ? String(ratio) : undefined,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--zirro-color-muted, #64748b)",
            fontSize: "14px",
            padding: "16px",
            textAlign: "center",
          }}
        >
          {ctx.mode === "edit" ? (
            <span data-edit-only>{alt || "Missing image"}</span>
          ) : (
            ""
          )}
        </div>
      </figure>
    );
  }

  const framed = ratio !== null || zoom > 1;
  const img = (
    <img
      src={src}
      alt={alt}
      onError={() => setFailedSrc(src)}
      style={{
        width: "100%",
        height: ratio ? "100%" : "auto",
        objectFit: ratio ? fit : undefined,
        objectPosition: ratio ? focus : undefined,
        transform: zoom > 1 ? `scale(${zoom})` : undefined,
        transformOrigin: zoom > 1 ? focus : undefined,
        borderRadius: framed ? undefined : radius,
        display: "block",
      }}
    />
  );
  const figure = (
    <figure {...dataProps} style={wrapper}>
      {framed ? (
        <div
          data-image-frame=""
          style={{
            width: "100%",
            aspectRatio: ratio ? String(ratio) : undefined,
            overflow: "hidden",
            borderRadius: radius,
            background:
              fit === "contain" && ratio
                ? "var(--zirro-color-surface, #f1f5f9)"
                : undefined,
          }}
        >
          {img}
        </div>
      ) : (
        img
      )}
    </figure>
  );

  if (props.href && typeof props.href === "object") {
    return (
      <SmartLink
        href={props.href as Href}
        ctx={ctx}
        style={{
          textDecoration: "none",
          display: "inline-block",
          width: "100%",
        }}
      >
        {figure}
      </SmartLink>
    );
  }

  return figure;
}

const BUTTON_VARIANTS: Record<string, CSSProperties> = {
  primary: {
    background: "var(--zirro-color-primary)",
    color: "#ffffff",
    border: "1px solid transparent",
  },
  secondary: {
    background: "var(--zirro-color-secondary)",
    color: "#ffffff",
    border: "1px solid transparent",
  },
  outline: {
    background: "transparent",
    color: "var(--zirro-color-primary)",
    border: "1px solid var(--zirro-color-primary)",
  },
  ghost: {
    background: "transparent",
    color: "var(--zirro-color-text)",
    border: "1px solid transparent",
  },
};

const BUTTON_SIZES: Record<string, { padding: string; fontSize: string }> = {
  sm: { padding: "6px 12px", fontSize: "14px" },
  md: { padding: "10px 18px", fontSize: "16px" },
  lg: { padding: "14px 26px", fontSize: "18px" },
};

export function Button({ node, ctx }: Common) {
  const { props, dataProps, style } = useProps(node, ctx);
  const href = (props.href ?? { type: "url", url: "#" }) as Href;
  const variant =
    BUTTON_VARIANTS[String(props.variant)] ?? BUTTON_VARIANTS.primary!;
  const size = BUTTON_SIZES[String(props.size)] ?? BUTTON_SIZES.md!;

  const customBg = resolveColorValue(props.backgroundColor);
  const customColor = resolveColorValue(props.textColor);
  const customBorderColor = resolveColorValue(props.borderColor);
  const customBorderWidth = props.borderWidth
    ? `${String(props.borderWidth).replace("px", "")}px`
    : undefined;
  const customRadius = props.borderRadius
    ? (RADII_VAL[String(props.borderRadius)] ?? String(props.borderRadius))
    : undefined;
  const customFontSize = props.fontSize
    ? fontSizeValue(props.fontSize)
    : undefined;
  const customWeight = props.weight ? Number(props.weight) : undefined;
  const customPadding = props.padding
    ? resolveSpacingValue(props.padding)
    : undefined;
  const mTop = props.marginTop
    ? resolveSpacingValue(props.marginTop)
    : undefined;
  const mBottom = props.marginBottom
    ? resolveSpacingValue(props.marginBottom)
    : undefined;

  const btnStyle: CSSProperties = {
    ...variant,
    ...size,
    backgroundColor: customBg ?? (variant.background as string),
    color: customColor ?? (variant.color as string),
    border:
      customBorderWidth || customBorderColor
        ? `${customBorderWidth ?? "1px"} solid ${customBorderColor ?? "currentColor"}`
        : variant.border,
    borderRadius: customRadius ?? "var(--zirro-radius, 8px)",
    fontSize: customFontSize ?? size.fontSize,
    fontWeight: customWeight ?? 600,
    padding: customPadding ?? size.padding,
    marginTop: mTop,
    marginBottom: mBottom,
    display: "inline-block",
    textDecoration: "none",
    fontFamily: "var(--zirro-font-body)",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  return (
    <span
      {...dataProps}
      style={{
        display: "inline-block",
        textAlign: (props.align as CSSProperties["textAlign"]) ?? "left",
        ...style,
      }}
    >
      <SmartLink href={href} ctx={ctx} style={btnStyle}>
        {String(props.label ?? "Button")}
      </SmartLink>
    </span>
  );
}

export interface NavLink {
  label: string;
  href: Href;
}

export function Header({ node, ctx }: Common) {
  const { props, dataProps } = useProps(node, ctx);
  const links = Array.isArray(props.links)
    ? (props.links as NavLink[]).filter(
        (l) => l.label?.trim().toLowerCase() !== "shop",
      )
    : [];
  const logoSrc =
    typeof props.logoSrc === "string" && props.logoSrc ? props.logoSrc : null;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const logoFailed = logoSrc !== null && failedSrc === logoSrc;
  // Below 768px the menu always collapses (CSS). Wider headers collapse to the
  // hamburger only when the logo, links and button don't fit on one line.
  const headerRef = useRef<HTMLElement>(null);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  const neededWidthRef = useRef(0);

  const logoType = String(props.logoType ?? "both"); // 'text' | 'image' | 'both'
  const logoHeight =
    typeof props.logoHeight === "number" ||
    (typeof props.logoHeight === "string" && props.logoHeight.trim())
      ? `${String(props.logoHeight).replace("px", "")}px`
      : "36px";
  const logoText = String(props.logoText ?? "My site");
  const cta = props.cta as { label: string; href: Href } | undefined;
  const navKey = `${logoText}|${logoType}|${logoSrc ?? ""}|${cta?.label ?? ""}|${links.map((l) => l.label).join("|")}`;

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    // Content changed: show the full menu again and re-measure.
    collapsedRef.current = false;
    setNavCollapsed(false);
    const check = () => {
      const nav = header.querySelector<HTMLElement>(
        ".zirro-header-nav-desktop",
      );
      const logo = header.querySelector<HTMLElement>(".zirro-header-logo");
      const controls = header.querySelector<HTMLElement>(
        ".zirro-header-controls",
      );
      const navShown = nav !== null && getComputedStyle(nav).display !== "none";
      if (!collapsedRef.current && navShown && logo && controls) {
        // What one line needs: the full logo, the controls, the gap between
        // them (24px) and the header's own padding.
        const styles = getComputedStyle(header);
        const needed =
          logo.scrollWidth +
          controls.scrollWidth +
          24 +
          parseFloat(styles.paddingLeft) +
          parseFloat(styles.paddingRight);
        if (needed <= header.clientWidth) return;
        neededWidthRef.current = needed;
        collapsedRef.current = true;
        setNavCollapsed(true);
      } else if (
        collapsedRef.current &&
        header.clientWidth >= neededWidthRef.current
      ) {
        collapsedRef.current = false;
        setNavCollapsed(false);
        setMobileMenuOpen(false);
      }
    };
    check();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(check);
    observer.observe(header);
    return () => observer.disconnect();
  }, [navKey]);

  const renderLogo = () => {
    const showImage =
      (logoType === "image" || logoType === "both") && logoSrc && !logoFailed;
    const showText =
      logoType === "text" ||
      logoType === "both" ||
      (logoType === "image" && (!logoSrc || logoFailed));

    return (
      <div
        className="zirro-header-logo"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {showImage ? (
          <img
            src={logoSrc!}
            alt={logoText}
            onError={() => setFailedSrc(logoSrc)}
            style={{
              height: logoHeight,
              maxHeight: "56px",
              width: "auto",
              maxWidth: "180px",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : null}
        {showText ? (
          <strong
            style={{
              color: "var(--zirro-color-text)",
              fontSize: "18px",
              fontWeight: 700,
              fontFamily: "var(--zirro-font-heading)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              // Never wider than the screen minus the menu button.
              maxWidth: "min(320px, calc(100vw - 112px))",
            }}
          >
            {logoText}
          </strong>
        ) : null}
      </div>
    );
  };

  return (
    <header
      {...dataProps}
      ref={headerRef}
      data-nav-collapsed={navCollapsed || undefined}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        padding: "14px 24px",
        borderBottom: "1px solid var(--zirro-color-border)",
        fontFamily: "var(--zirro-font-body)",
        background: "var(--zirro-color-background)",
        zIndex: 50,
      }}
    >
      <SmartLink
        href={{ type: "page", pageId: ctx.document.pages[0]?.id }}
        ctx={ctx}
        style={{
          textDecoration: "none",
          color: "inherit",
          display: "inline-flex",
          minWidth: 0,
        }}
      >
        {renderLogo()}
      </SmartLink>

      {/* Right controls */}
      <div
        className="zirro-header-controls"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          flexShrink: 0,
        }}
      >
        {/* Desktop navigation */}
        <div
          className="zirro-header-nav-desktop"
          style={{ display: "flex", alignItems: "center", gap: "20px" }}
        >
          <nav style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            {links.map((link, i) => (
              <SmartLink
                key={i}
                href={link.href}
                ctx={ctx}
                style={{
                  color: "var(--zirro-color-text)",
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {link.label}
              </SmartLink>
            ))}
          </nav>
          {cta ? (
            <SmartLink
              href={cta.href}
              ctx={ctx}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 600,
                borderRadius: "var(--zirro-radius, 8px)",
                background: "var(--zirro-color-primary)",
                color: "#ffffff",
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              {cta.label}
            </SmartLink>
          ) : null}
        </div>

        {/* Header action buttons (cart/wishlist) + mobile hamburger button */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {ctx.headerActions ?? null}
          <button
            type="button"
            className="zirro-header-hamburger"
            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: "38px",
              height: "38px",
              padding: 0,
              borderRadius: "var(--zirro-radius, 8px)",
              border: "1px solid var(--zirro-color-border, #e2e8f0)",
              background: "var(--zirro-color-surface, #f8fafc)",
              color: "var(--zirro-color-text, #0f172a)",
              cursor: "pointer",
            }}
          >
            {mobileMenuOpen ? (
              <X size={20} aria-hidden="true" />
            ) : (
              <Menu size={20} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen ? (
        <div
          className="zirro-header-drawer is-open"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "var(--zirro-color-background, #ffffff)",
            borderBottom: "1px solid var(--zirro-color-border, #e2e8f0)",
            boxShadow: "0 16px 32px rgba(0, 0, 0, 0.12)",
            padding: "16px 20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            zIndex: 100,
          }}
        >
          <nav style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {links.map((link, i) => (
              <SmartLink
                key={i}
                href={link.href}
                ctx={ctx}
                style={{
                  color: "var(--zirro-color-text)",
                  textDecoration: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  padding: "12px 14px",
                  borderRadius: "var(--zirro-radius, 8px)",
                  display: "block",
                }}
              >
                <span onClick={() => setMobileMenuOpen(false)}>
                  {link.label}
                </span>
              </SmartLink>
            ))}
          </nav>
          {cta ? (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "8px",
                borderTop: "1px solid var(--zirro-color-border, #e2e8f0)",
              }}
            >
              <SmartLink
                href={cta.href}
                ctx={ctx}
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "12px 16px",
                  fontSize: "15px",
                  fontWeight: 600,
                  borderRadius: "var(--zirro-radius, 8px)",
                  background: "var(--zirro-color-primary)",
                  color: "#ffffff",
                  textDecoration: "none",
                }}
              >
                <span onClick={() => setMobileMenuOpen(false)}>
                  {cta.label}
                </span>
              </SmartLink>
            </div>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

export function Footer({ node, ctx }: Common) {
  const { props, dataProps } = useProps(node, ctx);
  const links = Array.isArray(props.links) ? (props.links as NavLink[]) : [];
  const email = typeof props.email === "string" ? props.email.trim() : "";
  const phone = typeof props.phone === "string" ? props.phone.trim() : "";
  const copyright = String(props.copyright ?? "").replace(
    "{{year}}",
    String(new Date().getFullYear()),
  );
  return (
    <footer
      {...dataProps}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        alignItems: "center",
        padding: "24px",
        borderTop: "1px solid var(--zirro-color-border)",
        color: "var(--zirro-color-muted)",
        fontSize: "14px",
        fontFamily: "var(--zirro-font-body)",
        background: "var(--zirro-color-surface)",
        textAlign: "center",
      }}
    >
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        {links.map((link, i) => (
          <SmartLink
            key={i}
            href={link.href}
            ctx={ctx}
            style={{ color: "var(--zirro-color-muted)" }}
          >
            {link.label}
          </SmartLink>
        ))}
      </div>
      {email || phone ? (
        <div
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {email ? (
            <SmartLink
              href={{ type: "url", url: `mailto:${email}` }}
              ctx={ctx}
              style={{ color: "inherit" }}
            >
              {email}
            </SmartLink>
          ) : null}
          {phone ? (
            <SmartLink
              href={{
                type: "url",
                url: `tel:${phone.replace(/[^+0-9]/g, "")}`,
              }}
              ctx={ctx}
              style={{ color: "inherit" }}
            >
              {phone}
            </SmartLink>
          ) : null}
        </div>
      ) : null}
      <div>
        {String(props.businessName ?? "")} {copyright}
      </div>
    </footer>
  );
}

/** Renders a child node via the registry, handling unknown types. */
export function RenderChild({ node, ctx }: Common): ReactNode {
  if (ctx.renderSlot) {
    const slot = ctx.renderSlot(node);
    if (slot !== undefined) return slot;
  }
  const entry = getRegistryEntry(node.type);
  if (!entry) {
    if (ctx.mode !== "edit") return null; // unknown components are skipped (PRD §98)
    return (
      <div
        data-node-id={node.id}
        data-unknown="true"
        style={{
          border: "1px dashed #f59e0b",
          background: "rgba(245, 158, 11, 0.08)",
          padding: "12px",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#b45309",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Unknown component: {node.type}
      </div>
    );
  }
  return <entry.Component node={node} ctx={ctx} />;
}
